import { api, unwrapList } from '../../../shared/lib/axios';

export type ContractSituacao =
  | 'em_elaboracao'
  | 'em_revisao_juridica'
  | 'em_aprovacao'
  | 'aguardando_assinatura'
  | 'assinado'
  | 'vigente'
  | 'suspenso'
  | 'em_renovacao'
  | 'em_aditivo'
  | 'encerrado'
  | 'rescindido'
  | 'cancelado';

export interface ContractListItem {
  id: string;
  codigo: string | null;
  numero: string;
  clientId: string;
  situacao: ContractSituacao;
  responsavelComercialId: string;
  gestorContratoId: string;
  vigenciaInicial: string;
  vigenciaFinal: string;
  moeda: string;
  valorTotal: string | null;
  isRecorrente: boolean;
  modalidade?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Contract extends ContractListItem {
  grupoEconomicoId: string | null;
  unidadeId: string | null;
  proposalId: string | null;
  baseComercialId: string | null;
  dataAssinatura: string | null;
  justificativaManual: string | null;
  currentVersionId: string | null;
  previousSituacao: ContractSituacao | null;
  objeto: string | null;
  observacoes: string | null;
  modalidade: string | null;
  linhaNegocioId: string | null;
  renovadoDeContratoId: string | null;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface ListContractsParams {
  search?: string;
  clientId?: string;
  responsavelComercialId?: string;
  gestorContratoId?: string;
  situacao?: ContractSituacao;
  vigenciaFinalFrom?: string;
  vigenciaFinalTo?: string;
  page?: number;
  limit?: number;
}

export interface ListContractsResponse {
  data: ContractListItem[];
  pagination: { limit: number; offset: number; count: number; page: number; hasNext: boolean };
}

export interface CreateContractPayload {
  numero: string;
  clientId: string;
  responsavelComercialId: string;
  gestorContratoId: string;
  vigenciaInicial: string;
  vigenciaFinal: string;
  proposalId?: string;
  unidadeId?: string;
  objeto?: string;
  observacoes?: string;
  moeda?: string;
  valorTotal?: string;
  isRecorrente?: boolean;
  justificativaManual?: string;
}

export type UpdateContractPayload = Partial<Omit<CreateContractPayload, 'clientId'>>;

export const CONTRATOS_KEYS = {
  all: ['contratos'] as const,
  list: (params?: ListContractsParams) => ['contratos', 'list', params] as const,
  detail: (id: string) => ['contratos', 'detail', id] as const,
  history: (id: string) => ['contratos', 'history', id] as const,
  addenda: (id: string) => ['contratos', 'addenda', id] as const,
  balance: (id: string) => ['contratos', 'balance', id] as const,
  indicators: (params?: Record<string, unknown>) => ['contratos', 'indicators', params] as const,
};

function toQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function listarContratos(params?: ListContractsParams): Promise<ListContractsResponse> {
  const { data } = await api.get<ListContractsResponse>(
    `/contracts${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarContrato(id: string): Promise<Contract> {
  const { data } = await api.get<Contract>(`/contracts/${id}`);
  return data;
}

export async function criarContrato(payload: CreateContractPayload): Promise<Contract> {
  const { data } = await api.post<Contract>('/contracts', payload);
  return data;
}

export async function atualizarContrato(id: string, payload: UpdateContractPayload): Promise<Contract> {
  const { data } = await api.patch<Contract>(`/contracts/${id}`, payload);
  return data;
}

export async function atualizarSituacaoContrato(
  id: string,
  situacao: ContractSituacao,
  reason?: string,
): Promise<Contract> {
  const { data } = await api.post<Contract>(`/contracts/${id}/status`, { situacao, reason });
  return data;
}

export type RenewalMode = 'mesmo_contrato' | 'novo_contrato_vinculado';

export interface RenewContractPayload {
  renewalMode: RenewalMode;
  novaVigenciaFinal: string;
  novaVigenciaInicial?: string;
  reason?: string;
}

export interface RenewContractResult {
  id: string;
  codigo?: string | null;
  situacao: ContractSituacao;
  renewalMode: RenewalMode;
  renovadoDeContratoId?: string;
}

/** RN-COM-022: renova mantendo o mesmo contrato com novo período, ou gera um novo contrato vinculado. */
export async function renovarContrato(
  id: string,
  payload: RenewContractPayload,
): Promise<RenewContractResult> {
  const { data } = await api.post<RenewContractResult>(`/contracts/${id}/renew`, payload);
  return data;
}

export interface ContractHistoryEntry {
  id: string;
  contractId: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  actor: string | null;
  reason: string | null;
  createdAt: string;
}

/** Contexto da criação — `{ origem: 'proposta' | 'manual', ... }`. */
export interface ContractCreationContext {
  origem?: 'proposta' | 'manual';
  proposalId?: string;
  proposalCodigo?: string;
  proposalVersionId?: string;
  proposalVersionNumero?: string;
  importedFromProposal?: Record<string, string | number | boolean | null>;
  importConflicts?: Array<{ field: string; dtoValue: string; proposalValue: string }>;
  deferredFromProposal?: string[];
}

/** Mapeia a trilha crua do backend (`oldValue`/`newValue`/`userId`/`context`). */
export async function listarHistoricoContrato(
  id: string,
  params?: { limit?: number; offset?: number },
): Promise<{
  data: (ContractHistoryEntry & { context: ContractCreationContext | null })[];
  pagination: { count: number };
}> {
  const { data } = await api.get<{
    data: Array<Record<string, unknown>>;
    pagination: { limit: number; offset: number; count: number };
  }>(`/contracts/${id}/history${toQueryString(params as Record<string, unknown>)}`);

  return {
    data: (data.data ?? []).map((h) => ({
      id: h.id as string,
      contractId: h.contractId as string,
      action: h.action as string,
      before: (h.oldValue as Record<string, unknown> | null) ?? null,
      after: (h.newValue as Record<string, unknown> | null) ?? null,
      actor: (h.userId as string | null) ?? null,
      reason: (h.reason as string | null) ?? null,
      createdAt: h.createdAt as string,
      context: (h.context as ContractCreationContext | null) ?? null,
    })),
    pagination: { count: data.pagination?.count ?? 0 },
  };
}

/** A criação do contrato carrega o contexto da geração (RN-COM-003). */
export function extractCreationContext(
  history: { action: string; context: ContractCreationContext | null }[],
): ContractCreationContext | null {
  return history.find((h) => h.action === 'contrato.created')?.context ?? null;
}

export interface ContractBalance {
  id: string;
  contractId: string;
  dimension: 'financeiro' | 'horas' | 'recursos' | 'consumo';
  initialBalance: string;
  currentBalance: string;
  unit: string;
  alertThresholdPct: string | null;
}

export async function buscarSaldo(id: string): Promise<ContractBalance[]> {
  const { data } = await api.get<ContractBalance[]>(`/contracts/${id}/balance`);
  return data;
}

export interface ContractIndicators {
  totalContratos: number;
  totalPorSituacao: Record<ContractSituacao, number>;
  valorTotalContratado: number;
  receitaRecorrenteContratada: number;
  saldoDisponivelAgregado: number;
  contratosProximosVencimento: {
    dias7: number;
    dias15: number;
    dias30: number;
    dias60: number;
    dias90: number;
    dias180: number;
  };
  mediaAditvosPorContrato: number;
  tempoMedioAssinatura: number | null;
  contratosVigentesCount: number;
  contratosVencidosCount: number;
  valorMedioContrato: number | null;
  taxaRenovacao: number | null;
  contratosPorCliente: { clientId: string; count: number }[];
  contratosPorUnidade: { unidadeId: string | null; count: number }[];
  contratosPorResponsavel: { responsavelComercialId: string; count: number }[];
}

export type SignatureStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'rejected';

export interface SignatoryRecord {
  userId?: string;
  email: string;
  name: string;
  signedAt?: string;
  status: 'pending' | 'signed' | 'rejected';
  rejectionReason?: string;
}

export interface ContractSignatureMeta {
  id: string;
  contractId: string;
  provider: string;
  providerDocId: string;
  signatories: SignatoryRecord[];
  status: SignatureStatus;
  completedAt: string | null;
}

export interface SignatoryInput {
  email: string;
  name: string;
  userId?: string;
}

export async function enviarParaAssinatura(
  contractId: string,
  signatories: SignatoryInput[],
): Promise<ContractSignatureMeta> {
  const { data } = await api.post<ContractSignatureMeta>(
    `/contracts/${contractId}/signatures/send`,
    { signatories },
  );
  return data;
}

export async function registrarAssinaturaManual(
  contractId: string,
  signatories: SignatoryInput[],
): Promise<ContractSignatureMeta> {
  const { data } = await api.post<ContractSignatureMeta>(
    `/contracts/${contractId}/signatures/register-manual`,
    { signatories },
  );
  return data;
}

export async function buscarStatusAssinatura(contractId: string): Promise<ContractSignatureMeta | null> {
  const { data } = await api.get<ContractSignatureMeta | null>(
    `/contracts/${contractId}/signatures/status`,
  );
  return data;
}

/** Assinatura do signatário interno (tem conta no sistema) — identidade já provada pela sessão. */
export async function assinarComoSignatarioInterno(contractId: string): Promise<ContractSignatureMeta> {
  const { data } = await api.post<ContractSignatureMeta>(`/contracts/${contractId}/signatures/sign`);
  return data;
}

export async function buscarIndicadores(params?: {
  clientId?: string;
  unidadeId?: string;
  periodFrom?: string;
  periodTo?: string;
}): Promise<ContractIndicators> {
  const { data } = await api.get<ContractIndicators>(
    `/contracts/indicators${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

// ------------------------------------------------------------------
// Geração a partir da proposta (RN-COM-003)
// ------------------------------------------------------------------

export interface ProposalImportCondition {
  field: string;
  label: string;
  ok: boolean;
  actual: string | null;
}

/** Campos que a proposta supre na geração do contrato. */
export interface ProposalImportFields {
  clientId?: string | null;
  responsavelComercialId?: string | null;
  grupoEconomicoId?: string | null;
  valorTotal?: string | null;
  vigenciaFinal?: string | null;
  observacoes?: string | null;
  objeto?: string | null;
  baseComercialId?: string | null;
}

export interface ProposalPreview {
  proposalId: string;
  proposalCodigo: string;
  versionId: string | null;
  versionNumero: string | null;
  status: string | null;
  canGenerate: boolean;
  blockedBy: ProposalImportCondition[];
  fields: ProposalImportFields;
  deferred: string[];
}

/**
 * Pré-visualiza a geração. Sempre 200: `canGenerate` + `blockedBy`
 * informam se o botão de gerar deve ficar habilitado.
 * `clientConditions` permite ao formulário reportar a situação do
 * cliente escolhido para que a lista venha completa.
 */
export async function previewGeracaoContrato(
  proposalId: string,
  clientConditions: ProposalImportCondition[] = [],
): Promise<ProposalPreview> {
  const { data } = await api.post<ProposalPreview>(
    `/contracts/proposals/${proposalId}/preview`,
    { clientConditions },
  );
  return data;
}

/** Validação dura — 422 com a lista quando alguma condição falha. */
export async function validarPropostaParaContrato(
  proposalId: string,
  clientId?: string,
): Promise<{ proposalId: string }> {
  const { data } = await api.post<{ proposalId: string }>(
    `/contracts/proposals/${proposalId}/validate`,
    { clientId },
  );
  return data;
}

// ======================================================================
// Cláusulas (RN-COM-029)
// ======================================================================

export interface ContractClause {
  id: string;
  contractId: string;
  templateId: string | null;
  slug: string;
  title: string;
  content: string;
  isMandatory: boolean;
  order: number;
  createdAt: string;
}

export interface ClauseTemplate {
  id: string;
  name: string;
  slug: string;
  modality: string;
  description: string | null;
  template: string | null;
  isMandatory: boolean;
  isActive: boolean;
}

export async function listarClausulas(contractId: string): Promise<ContractClause[]> {
  const { data } = await api.get<ContractClause[]>(`/contracts/${contractId}/clauses`);
  return unwrapList(data);
}

export async function listarTemplatesClausulas(
  contractId: string,
  modality?: string,
): Promise<ClauseTemplate[]> {
  const { data } = await api.get<ClauseTemplate[]>(`/contracts/${contractId}/clauses/templates`, {
    params: { modality },
  });
  return unwrapList(data);
}

export interface AddClausePayload {
  slug: string;
  title: string;
  content: string;
  isMandatory?: boolean;
  order?: number;
  templateId?: string;
}

export async function adicionarClausula(
  contractId: string,
  payload: AddClausePayload,
): Promise<ContractClause> {
  const { data } = await api.post<ContractClause>(`/contracts/${contractId}/clauses`, payload);
  return data;
}

export async function removerClausula(contractId: string, clauseId: string): Promise<void> {
  await api.delete(`/contracts/${contractId}/clauses/${clauseId}`);
}

// ======================================================================
// Documentos/Anexos (RN-COM-011)
// ======================================================================

export interface ContractAttachment {
  id: string;
  contractId: string;
  documentType: string;
  name: string;
  fileSize: number;
  mimeType: string;
  isActive: boolean;
  supersededById: string | null;
  createdAt: string;
}

export async function listarAnexos(contractId: string): Promise<ContractAttachment[]> {
  const { data } = await api.get<ContractAttachment[]>(`/contracts/${contractId}/attachments`);
  return unwrapList(data);
}

export async function uploadAnexo(
  contractId: string,
  payload: { documentType: string; file: File },
): Promise<ContractAttachment> {
  const fd = new FormData();
  fd.append('documentType', payload.documentType);
  fd.append('file', payload.file);
  const { data } = await api.post<ContractAttachment>(`/contracts/${contractId}/attachments`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function removerAnexo(contractId: string, attachmentId: string): Promise<void> {
  await api.delete(`/contracts/${contractId}/attachments/${attachmentId}`);
}

// ======================================================================
// Aditivos (RN-COM-006/007/017)
// ======================================================================

export interface AddendumApprover {
  id: string;
  addendumId: string;
  userId: string;
  decision: 'aprovado' | 'rejeitado' | null;
  justificativa: string | null;
  decidedAt: string | null;
}

export interface ScopeChange {
  type: 'vigencia' | 'valor' | 'escopo' | 'reajuste' | 'clausulas' | 'quantitativos' | 'anexos';
  descricao?: string;
  vigenciaFinalNova?: string;
  valorTotalNovo?: string;
  objeto?: string;
}

export interface Addendum {
  id: string;
  contractId: string;
  code: string;
  versionNumber: number;
  justificativa: string;
  scopeChanges: ScopeChange[];
  situacao: 'em_elaboracao' | 'em_aprovacao' | 'aprovado' | 'rejeitado' | 'concluido' | 'cancelado';
  vigenciaInicial: string | null;
  createdAt: string;
  approvers: AddendumApprover[];
}

export async function listarAditivos(contractId: string): Promise<Addendum[]> {
  const { data } = await api.get<Addendum[]>(`/contracts/${contractId}/addenda`);
  return unwrapList(data);
}

export interface CreateAddendumPayload {
  justificativa: string;
  scopeChanges: ScopeChange[];
  approverUserIds: string[];
  vigenciaInicial?: string;
}

export async function criarAditivo(contractId: string, payload: CreateAddendumPayload): Promise<Addendum> {
  const { data } = await api.post<Addendum>(`/contracts/${contractId}/addenda`, payload);
  return data;
}

export async function aprovarAditivo(
  contractId: string,
  addendumId: string,
  observacoes?: string,
): Promise<Addendum> {
  const { data } = await api.post<Addendum>(
    `/contracts/${contractId}/addenda/${addendumId}/approve`,
    { observacoes },
  );
  return data;
}

export async function rejeitarAditivo(
  contractId: string,
  addendumId: string,
  justificativa: string,
): Promise<Addendum> {
  const { data } = await api.post<Addendum>(
    `/contracts/${contractId}/addenda/${addendumId}/reject`,
    { justificativa },
  );
  return data;
}

export async function concluirAditivo(contractId: string, addendumId: string): Promise<Addendum> {
  const { data } = await api.post<Addendum>(`/contracts/${contractId}/addenda/${addendumId}/complete`);
  return data;
}

// ======================================================================
// Modelos de contrato (RN-COM-027, administração)
// ======================================================================

export interface ContractModel {
  id: string;
  name: string;
  description: string | null;
  serviceType: string | null;
  clientId: string | null;
  unitId: string | null;
  content: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

export async function listarModelos(filters?: {
  serviceType?: string;
  clientId?: string;
  unitId?: string;
  isActive?: boolean;
}): Promise<ContractModel[]> {
  const { data } = await api.get<ContractModel[]>('/contract-models', { params: filters });
  return unwrapList(data);
}

export interface ContractModelPayload {
  name: string;
  description?: string;
  serviceType?: string;
  clientId?: string;
  unitId?: string;
  content?: string;
}

export async function criarModelo(payload: ContractModelPayload): Promise<ContractModel> {
  const { data } = await api.post<ContractModel>('/contract-models', payload);
  return data;
}

export async function atualizarModelo(
  id: string,
  payload: Partial<ContractModelPayload> & { isActive?: boolean },
): Promise<ContractModel> {
  const { data } = await api.patch<ContractModel>(`/contract-models/${id}`, payload);
  return data;
}

export async function desativarModelo(id: string): Promise<{ id: string; deactivated: true }> {
  const { data } = await api.delete<{ id: string; deactivated: true }>(`/contract-models/${id}`);
  return data;
}

// ======================================================================
// Configuração de alertas por contrato (RN-COM-010)
// ======================================================================

export interface ContractAlertConfig {
  id: string;
  contractId: string | null;
  daysBefore: number;
  alertType: string;
  isActive: boolean;
  isGlobal: boolean;
  createdAt: string;
}

export async function listarConfigAlertas(contractId: string): Promise<ContractAlertConfig[]> {
  const { data } = await api.get<ContractAlertConfig[]>(`/contracts/${contractId}/alert-configs`);
  return unwrapList(data);
}

export async function criarConfigAlerta(
  contractId: string,
  payload: { daysBefore: number; alertType?: string },
): Promise<ContractAlertConfig> {
  const { data } = await api.post<ContractAlertConfig>(
    `/contracts/${contractId}/alert-configs`,
    payload,
  );
  return data;
}

export async function desativarConfigAlerta(
  contractId: string,
  configId: string,
): Promise<{ id: string; deactivated: true }> {
  const { data } = await api.delete<{ id: string; deactivated: true }>(
    `/contracts/${contractId}/alert-configs/${configId}`,
  );
  return data;
}

export async function listarAlertasGlobais(): Promise<ContractAlertConfig[]> {
  const { data } = await api.get<ContractAlertConfig[]>('/contract-alert-configs/global');
  return unwrapList(data);
}

export async function criarAlertaGlobal(payload: {
  daysBefore: number;
  alertType?: string;
}): Promise<ContractAlertConfig> {
  const { data } = await api.post<ContractAlertConfig>('/contract-alert-configs/global', payload);
  return data;
}

export async function desativarAlertaGlobal(configId: string): Promise<{ id: string; deactivated: true }> {
  const { data } = await api.delete<{ id: string; deactivated: true }>(
    `/contract-alert-configs/global/${configId}`,
  );
  return data;
}

// ======================================================================
// Administração do catálogo de templates de cláusula (RN-COM-029)
// ======================================================================

export async function listarTemplatesClausulasAdmin(filters?: {
  modality?: string;
  isActive?: boolean;
}): Promise<ClauseTemplate[]> {
  const { data } = await api.get<ClauseTemplate[]>('/contract-clause-templates', { params: filters });
  return unwrapList(data);
}

export interface ClauseTemplatePayload {
  name: string;
  slug: string;
  modality: string;
  description?: string;
  template?: string;
  isMandatory?: boolean;
  sortOrder?: number;
}

export async function criarTemplateClausula(payload: ClauseTemplatePayload): Promise<ClauseTemplate> {
  const { data } = await api.post<ClauseTemplate>('/contract-clause-templates', payload);
  return data;
}

export async function atualizarTemplateClausula(
  id: string,
  payload: Partial<ClauseTemplatePayload> & { isActive?: boolean },
): Promise<ClauseTemplate> {
  const { data } = await api.patch<ClauseTemplate>(`/contract-clause-templates/${id}`, payload);
  return data;
}

export async function desativarTemplateClausula(id: string): Promise<{ id: string; deactivated: true }> {
  const { data } = await api.delete<{ id: string; deactivated: true }>(`/contract-clause-templates/${id}`);
  return data;
}

// ======================================================================
// Reenvio de convite de assinatura (signatário externo)
// ======================================================================

export async function reenviarConviteAssinatura(
  contractId: string,
  email: string,
): Promise<{ sent: true }> {
  const { data } = await api.post<{ sent: true }>(`/contracts/${contractId}/signatures/resend-invite`, {
    email,
  });
  return data;
}
